import { ActivatedRoute, NavigationEnd, Params, Router } from "@angular/router";

import { TradableListService } from "../services/currency/tradable_list.service";
import { WalletComponent } from "./wallet.component";
import { Tradable } from "@models/interfaces/tradable";
import { TradableWallet } from "@models/interfaces/tradable_wallet";
import { TradableWalletListService } from "../services/wallet/tradable_wallet_list.service";
import { TradableWalletService } from "../services/wallet/tradable_wallet.service";
import { Fiat } from "@models";
import { TransferModalComponent } from "./step-summary/transfer-modal/transfer-modal.component";
import { Directive, inject } from "@angular/core";
import { TransactionWrapperModalComponent } from "../../transaction/modals/transaction-wrapper-modal/transaction-wrapper-modal.component";
import { WalletRenameComponent } from "./wallet-edit/wallet-rename/wallet-rename.component";
import { convertTradableToFiat } from "@helpers/tradable-convert";
import { InternalTransaction, TradableTransaction } from "@models/transaction";
import { AccountService } from "../../account/services/account.service";
import { TradablePrecisionPipe } from "../../core/pipes";
import { TranslationService } from "@core-services/translation.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { BFFAssetsService } from "@core-services/bff-assets.service";
import { EMPTY, switchMap, take } from "rxjs";
import { BFFSecurityAsset } from "@models/asset/bff_security_asset";
import { InitAssetsService } from "@core-services/init-assets.service";
import { IframeContextService } from "@modules/iframe-provider/iframe-context.service";

@UntilDestroy()
@Directive()
export abstract class TradableWalletComponent<
	T extends TradableTransaction,
	C extends Tradable,
	W extends TradableWallet
> extends WalletComponent<T, C, W> {
	protected transactionDateFormat: string = "LL";

	protected tradablePrecisionPipe: TradablePrecisionPipe = null;
	protected buttonActions: string[] = [];
	protected isRealSecurity = false;

	protected abstract walletService: TradableWalletService<W, T, C>;

	private bff = inject(BFFAssetsService);
	private initAssetsService = inject(InitAssetsService);
	protected readonly isIframeMode = inject(IframeContextService).isActive;

	protected constructor(
		protected route: ActivatedRoute,
		protected router: Router,
		protected tradableListService: TradableListService,
		protected walletListService: TradableWalletListService,
		protected translationService: TranslationService,
		protected accountService: AccountService,
		protected activatedRoute: ActivatedRoute
	) {
		super(activatedRoute);
		this.tradablePrecisionPipe = new TradablePrecisionPipe(tradableListService);
		let sub = null;
		sub = this.router.events.subscribe((event) => {
			if (event instanceof NavigationEnd) {
				const url = event.url;
				if (url.split("/")[1] !== "wallet") {
					sub.unsubscribe();
					if (this.walletService) {
						this.walletService.wallet = null;
					}
				}
			}
		});
		this.route.params.forEach((params: Params) => {
			this.walletId = params["pid"];
			if (!this.walletService) {
				sub.unsubscribe();
				this.infoService.setError(this.translationService.translate("wrong_wallet_id_redirect_error"));
				this.router.navigateByUrl("/portfolio");
				return;
			}
			this.walletService.wallet = this.getWallet();
			this.setCurrency();
			this.tid = params["tid"];
		});
	}

	public get lang(): string {
		return this.translationService.getLanguage();
	}

	public getButtonActions(): string[] {
		return this.buttonActions; // Set once on init();
	}

	protected init(requestUrl: string): void {
		let url: string = requestUrl;
		this.route.params.forEach((params: Params) => {
			if (!!params["pid"] && !!this.walletService.wallet) {
				const walletId: string = params["pid"];
				if (walletId !== this.walletService.wallet.getId()) {
					url = "wallet/" + walletId + "/transactions";
				}
			}
			this.initPagination(url, this.walletService.TxType);
			if (params["fpid"]) {
				this.openTransaction(params);
			}
		});
		this.setupWallet();
		this.buttonActions = this.getCurrency()?.getTradableActions() ?? [];
	}

	public setupWallet(): void {
		this.setTransactionDateFormat(this.transactionDateFormat);
		this.walletListService
			.getList()
			.pipe(
				untilDestroyed(this),
				switchMap(() => {
					this._wallet = this.walletService.getItemById(this.walletId);
					const id = (this._wallet as TradableWallet).getCurrencyId();
					const tradable = this.tradableListService.getItemById(id);
					if (!tradable) {
						return this.bff.getAssetFetch({ input: { idInternal: Number(id) } }).pipe(take(1));
					} else if (tradable instanceof BFFSecurityAsset) {
						// In cases where we already have the BFFAsset fetched, we could still miss it in the assets service
						// This function internally does `upsert` on the lists, so it's safe to be called here
						// even if the previous page added the asset already
						this.initAssetsService.addSecurityAsset(tradable);
						this.isRealSecurity = true;
					}
					return EMPTY;
				})
			)
			.subscribe({
				next: (assetFragment) => {
					const parsed = new BFFSecurityAsset(assetFragment);
					this.initAssetsService.addSecurityAsset(parsed);
					this.tradableListService.upsert(parsed);
					this.setCurrency();
					this.walletService.wallet = this._wallet;
					this.isRealSecurity = true;
				},
				error: () => {
					console.error("Setup wallet error");
				},
				complete: () => {
					console.warn("Setup wallet completed");
				}
			});
	}

	public transfer(): void {
		const t = new InternalTransaction();
		t["preselected"] = this.getWallet();
		this.modalService.create(TransferModalComponent, { data: t }, { showCloseButton: false }).present();
	}

	public get fiat(): Fiat {
		return this.accountService.getDefaultFiat() || new Fiat();
	}

	public openTransaction(params: Params): void {
		this.walletService.getTransaction(this.walletId, params["fpid"]).subscribe((tx) => {
			this.modalService
				.create(TransactionWrapperModalComponent, {
					transaction: tx,
					canOpen: false
				})
				.present();
			this.router.navigate(["wallet", params["pid"]]);
		});
	}
	public toFiat(c: number): number {
		return convertTradableToFiat(this.getCurrency(), c, this.fiat);
	}
	public getCssClass(): string {
		return this.getCurrency().getIconCssName(true);
	}

	public get cid(): string {
		return this.getCurrency().getId();
	}
	public openWalletDetails(): void {
		this.modalService.create(WalletRenameComponent, { data: { wallet: this.getWallet() } }).present();
	}
}
